import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { buildMyPurchaseItem } from '@/features/wallet/utils/mappers';
import {
  purchaseRepository,
  PURCHASES_PAGE_SIZE,
} from '@/features/wallet/repositories/supabaseWalletRepository';
import type { RollReviewStatus } from '@/types/wallet';

export interface MyPurchaseItem {
  id: string;
  instructor_name: string;
  instructor_avatar_url?: string;
  order_ref: string;
  date: string;
  amount_cents: number;
  currency: string;
  status: RollReviewStatus;
  deadline_at: string | null;
  refunded_at: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
}

/**
 * Infinite-query backed list of the practitioner's purchases.
 *
 * Each page fetches `PURCHASES_PAGE_SIZE` rows; consumers call `fetchNextPage`
 * to load the next slice. SOS-311 acceptance: historical payments accessible
 * (pagination or lazy load).
 */
export function useMyPurchases() {
  const { user } = useAuth();

  return useInfiniteQuery({
    queryKey: ['my-purchases', user?.id],
    enabled: !!user?.id,
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<MyPurchaseItem[]> => {
      if (!user?.id) throw new Error('Not authenticated');
      const rows = await purchaseRepository.fetchMyPurchases(user.id, {
        pageSize: PURCHASES_PAGE_SIZE,
        offset:   pageParam,
      });
      return rows.map(buildMyPurchaseItem);
    },
    getNextPageParam: (lastPage, allPages) => {
      // Stop paginating when the last page returned fewer rows than the page size.
      if (lastPage.length < PURCHASES_PAGE_SIZE) return undefined;
      return allPages.length * PURCHASES_PAGE_SIZE;
    },
  });
}
